import { Issuer as CheckoutIssuer, PaymentMethod, PaymentState } from '@zooplus/checkout-state';

import { creditCardsFromCheckout } from '@config/consts';
import { Issuer, Nullable, PaymentMethodStatus } from '@types-internal';
import { PreferredPayment } from '@types-internal/CheckoutTypes';
import {
  PaymentMethodShortName,
  flippedSupportedPMs,
  bancontactPaymentMethods,
  BLIK_ISSUER_ID,
} from '@types-internal/ModernizePaymentTypes';
import { TrackingFn } from '@types-internal/TrackingTypes';

/**
 * Utility functions for managing and formatting payment methods within a checkout or payment system.
 * Functions include:
 * - checkApplePaySupported
 * - formatPaymentLists
 * - generatePreferredPayment
 * - getInitialSelectedPayment
 * - getInitialPaymentMethods
 * - getPaymentMethodStatus
 * - isPaymentMethodDisabled
 * - onPaymentMethodClick
 * - separatePaymentLists
 * - trackChangePM
 * - getBackendPaymentMethod
 * - injectStandaloneBlik
 * - isSameIssuerSelected
 *
 * PS. When adding new functions, please consider keeping them in alphabetical order.
 */

/**
 * Function to check whether Apple Pay is supported in the browser.
 * This function checks if the `ApplePaySession` is available in the `window` object
 * and if the browser can make payments using Apple Pay.
 *
 * canMakePayments() can still throw an error `Trying to start an Apple Pay session from an insecure document`,
 * so we need to catch it and return false
 *
 * @returns {boolean} True if Apple Pay is supported and can make payments, otherwise false.
 */

export const checkApplePaySupported = (): boolean => {
  const ApplePay = globalThis.window.ApplePaySession;
  try {
    return !!(ApplePay && ApplePaySession.canMakePayments());
  } catch (e) {
    return false;
  }
};

/**
 * Function to return mock list of payments for each country with payment details
 *
 * @param countryToPaymentMethodMap list of countries and payment codes for each country
 * @param issuers the list of issuers for Ideal and Online Banking PL
 * @param disabledPayment the payment method should be disabled
 * @param autoshipmentEligiblePayment the list of autoshipment eligible payment methods
 * @param notAvailableForGuestPayments the list of payments that aren't available for guest
 * @param psp psp name
 * @param PMConfig payment setup info
 * @returns list of payments in a similar format that documented here
 */

export const formatPaymentLists = (
  countryToPaymentMethodMap: any,
  issuers: any,
  disabledPayment?: any,
  autoshipmentEligiblePayments?: any,
  notAvailableForGuestPayments?: any,
  psp?: string,
  PMConfig?: any,
  preferredPayment?: any,
) => {
  const countryCodes = Object.keys(countryToPaymentMethodMap);
  const paymentMethodList: any = {};
  const paymentSetupInfo = psp === 'ADYEN' ? PMConfig.Adyen : PMConfig.Braintree;
  const config: any = {
    OP: { issuers, lastIssuer: null },
    BC: { paymentSetupInfo: PMConfig.Bancontact },
    GP: {
      creditCardBrands: null,
      cards: null,
      paymentSetupInfo: null,
      lastIssuer: null,
      issuers: null,
      paymentAdjustment: null,
      bankAccountDetails: null,
      adyenPaymentMethodResponse: {
        paymentPartnerConnector: null,
        paymentMethods: [
          {
            name: 'Google Pay',
            type: 'googlepay',
            configuration: {
              merchantId: 'BCR2DN5T53V57M2X',
              gatewayMerchantId: 'ZooplusGooglePaySEK',
            },
            paymentMethod: 'GOOGLE_PAY',
            available: true,
            logos: {
              normal:
                'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/large/googlepay.png',
              small:
                'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/medium/googlepay.png',
              tiny: 'https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/small/googlepay.png',
            },
            issuers: [],
            brands: ['discover', 'mc', 'visa'],
            storedPaymentMethods: null,
          },
        ],
        countryCode: 'SE',
      },
      firstCard: null,
    },
    BS: { bankAccountDetails: { number: '1234', owner: 'John Doe' } },
    K: {
      paymentSetupInfo,
      cards: creditCardsFromCheckout,
      creditCardBrands: [
        'american_express',
        'carte_bancaire',
        'diners_club',
        'maestro',
        'master_card',
        'visa',
        'bcmc',
      ],
    },
    RS: {
      bankAccountDetails: null,
      storedBankAccounts: [
        {
          pspTokenIdentifier: 'W8G7TB8W7ZB9KX65',
          iban: '******************0400',
          bankName: 'placeholder ban',
          favorite: false,
        },
      ],
    },
  };

  countryCodes.forEach((country: any) => {
    paymentMethodList[country] = countryToPaymentMethodMap[country].map(
      (payment: PaymentMethodShortName, key: number) => ({
        paymentMethod: flippedSupportedPMs[payment],
        paymentMethodShortName: payment,
        autoshipmentEligible: autoshipmentEligiblePayments.includes(payment),
        availableForGuest: !notAvailableForGuestPayments.includes(payment),
        order: key + 1,
        preferredPayment: payment === preferredPayment,
        disabled: disabledPayment && disabledPayment.includes(payment),
        default: key === 0,
        details: config[payment] || {},
      }),
    );
  });

  return paymentMethodList;
};

/**
 * Function to return preferred payment object based on payment short name and some details
 *
 * @param paymentMethodShortName the payment short name
 * @param issuer the issuer info
 * @param creditCard the card Info
 * @returns preferred payment object
 */

export const generatePreferredPayment = (
  paymentMethod: PaymentMethodShortName,
  issuer?: Issuer,
  cards?: any,
  sepaInfo?: any,
) => {
  return {
    paymentMethod: flippedSupportedPMs[paymentMethod],
    paymentMethodShortName: paymentMethod,
    ...(issuer && { details: { lastIssuer: issuer } }),
    ...(cards && {
      details: {
        cards,
      },
    }),
    ...(sepaInfo && {
      details: {
        bankAccountDetails: sepaInfo,
      },
    }),
  };
};

/**
 * Function to get the initial selected payment method.
 *
 * This function determines the initial selected payment method based on the following priority order:
 * 1. If autoshipment is enabled:
 *    - Preferred payment method eligible for autoshipment, if not disabled.
 *    - Default payment method eligible for autoshipment, if not disabled.
 *    - The first available autoshipment-eligible payment method, if no preferred or default payment methods are autoshipment eligible.
 * 2. If autoshipment is not enabled:
 *    - The preferred payment method if it's not disabled.
 *    - The default payment method if it's not disabled.
 *    - The first available payment method that is not disabled.
 *
 * @param paymentMethods - An array of available payment methods.
 * @param customerType - The type of customer: 'EC' (Existing Customer), 'NC' (New Customer), 'GC' (Guest Customer), or '' (Not Specified).
 * @param preferred - The preferred payment method identifier.
 * @param autoshipmentReady - Whether autoshipment is ready and eligible for processing.
 *
 * @returns The initially selected payment method, or `undefined` if no suitable payment method is found.
 */

export const getInitialSelectedPayment = (
  paymentMethods: PaymentMethod[],
  customerType: string,
  preferred?: string,
  autoshipmentReady = false,
  cartHasMarketplaceItems = false,
) => {
  const defaultPayment = paymentMethods.find(pm => pm.default);
  const preferredPayment = paymentMethods.find(
    pm => pm.paymentMethodShortName === preferred || pm.preferredPayment,
  );

  const isEnabled = (pm: PaymentMethod) =>
    !isPaymentMethodDisabled(pm, customerType, cartHasMarketplaceItems);

  const isDefaultRivertyPreferred = (shortName: 'RI' | 'RS') =>
    defaultPayment?.paymentMethodShortName === shortName &&
    !preferredPayment &&
    isEnabled(defaultPayment);

  if (isDefaultRivertyPreferred('RI') || isDefaultRivertyPreferred('RS')) {
    return defaultPayment;
  }

  const preferredForCompatibility =
    preferredPayment ?? ({ paymentMethodShortName: preferred } as PreferredPayment);
  if (isIncompatibleWithCart(preferredForCompatibility, !!autoshipmentReady)) {
    return undefined;
  }

  // If order autoshipment is eligible, prioritize eligible payment methods with autoshipment eligibility
  if (autoshipmentReady && !preferredPayment) {
    return resolveAutoshipmentPayment(paymentMethods, defaultPayment, isEnabled);
  }

  // If autoshipment is not enabled, continue with standard priority
  return resolveStandardPayment(paymentMethods, preferredPayment, defaultPayment, isEnabled);
};

const resolveAutoshipmentPayment = (
  paymentMethods: PaymentMethod[],
  defaultPayment: PaymentMethod | undefined,
  isEnabled: (pm: PaymentMethod) => boolean,
) => {
  if (defaultPayment && defaultPayment.autoshipmentEligible && isEnabled(defaultPayment)) {
    return defaultPayment;
  }
  return paymentMethods.find(pm => pm.autoshipmentEligible && isEnabled(pm));
};

const resolveStandardPayment = (
  paymentMethods: PaymentMethod[],
  preferredPayment: PaymentMethod | undefined,
  defaultPayment: PaymentMethod | undefined,
  isEnabled: (pm: PaymentMethod) => boolean,
) => {
  if (preferredPayment && isEnabled(preferredPayment)) {
    return preferredPayment;
  }
  if (defaultPayment && isEnabled(defaultPayment)) {
    return defaultPayment;
  }
  return paymentMethods.find(isEnabled);
};

/**
 * Function to map the internally-selected payment method to the value that must be sent to
 * checkout when saving the payment method.
 *
 * The standalone BLIK feature surfaces BLIK as its own virtual payment method (short name
 * `BLK`), but the backend still expects it as an Online Banking PL (`OP`) issuer. So whenever
 * the selected method is `BLK`, it is sent as `OP` with the BLIK issuer kept in
 * `details.lastIssuer`; every other method is passed through unchanged.
 *
 * It maps any payment held in the virtual shape, a stored preference included, so that both sides
 * of a comparison can be brought into the representation checkout works with.
 *
 * @param selectedPayment - The payment method to map (may be the virtual `BLK`)
 * @returns The paymentMethod/paymentMethodShortName/details to send to checkout
 */
export const getBackendPaymentMethod = (
  selectedPayment?: PreferredPayment | Nullable<PaymentMethod>,
): Pick<PaymentMethod, 'paymentMethod' | 'paymentMethodShortName' | 'details'> => {
  if (selectedPayment?.paymentMethodShortName === 'BLK') {
    return {
      paymentMethod: flippedSupportedPMs.OP,
      paymentMethodShortName: 'OP',
      details: {
        ...selectedPayment.details,
        lastIssuer: selectedPayment.details?.lastIssuer,
      },
    };
  }
  return {
    paymentMethod: selectedPayment?.paymentMethod ?? null,
    paymentMethodShortName: selectedPayment?.paymentMethodShortName ?? null,
    ...(selectedPayment?.details && { details: selectedPayment.details }),
  };
};

/**
 * Checks whether the given issuer is the BLIK issuer within the Online Banking PL list.
 */
export const isBlikIssuer = (issuer?: CheckoutIssuer | Issuer | null): boolean =>
  issuer?.issuerId === BLIK_ISSUER_ID;

/**
 * Whether the given payment is P24-BLIK, which only supports one-time orders and so can never
 * carry an autoshipment order.
 *
 * It shows up in two shapes depending on the standalone BLIK toggle: as the virtual `BLK` method
 * when the feature is on, and as Online Banking PL (`OP`) with BLIK as its issuer when it is off.
 */
export const isOneTimeOnlyBlik = (
  payment?: PreferredPayment | Nullable<PaymentMethod>,
): boolean => {
  if (!payment) {
    return false;
  }
  if (payment.paymentMethodShortName === 'BLK') {
    return true;
  }
  return payment.paymentMethodShortName === 'OP' && isBlikIssuer(payment.details?.lastIssuer);
};

/**
 * Whether the given payment cannot carry this kind of order.
 */
/**
 * Whether the given payment is BLIK Autoship (`BL`), which only carries autoshipment orders and so
 * can never carry a one-time order. The mirror image of {@link isOneTimeOnlyBlik}.
 */
export const isAutoshipOnlyBlik = (payment?: PreferredPayment | Nullable<PaymentMethod>): boolean =>
  payment?.paymentMethodShortName === 'BL';

const getIssuerId = (payment?: PreferredPayment | Nullable<PaymentMethod>) => {
  if (payment?.paymentMethodShortName === 'BLK') {
    return BLIK_ISSUER_ID;
  }
  return payment?.details?.lastIssuer?.issuerId ?? undefined;
};

export const isSameIssuerSelected = (
  preferredPayment?: PreferredPayment,
  selectedPayment?: Nullable<PaymentMethod>,
): boolean => getIssuerId(preferredPayment) === getIssuerId(selectedPayment);

export const isIncompatibleWithCart = (
  payment: PreferredPayment | Nullable<PaymentMethod>,
  cartHasAutoshipmentEnabled: boolean,
): boolean => {
  if (cartHasAutoshipmentEnabled) {
    return isOneTimeOnlyBlik(payment);
  }
  return isAutoshipOnlyBlik(payment);
};

/**
 * Whether the customer has to pick a payment method before this order can go through, because the
 * one they prefer cannot carry it. Their preference is deliberately left unselected
 */
export const mustChoosePaymentMethod = (checkoutState?: {
  cartHasAutoshipmentEnabled?: boolean;
  preferredPayment?: PreferredPayment;
}): boolean =>
  isIncompatibleWithCart(
    checkoutState?.preferredPayment,
    !!checkoutState?.cartHasAutoshipmentEnabled,
  );

/**
 * When the standalone BLIK feature is enabled, split BLIK out of the Online Banking PL (OP)
 * issuer list into its own virtual payment method (short name `BLK`), rendered as a separate
 * tab immediately before OP. The virtual method keeps BLIK as its selected issuer; on submit
 * it is mapped back to OP with issuer = BLIK (see {@link getBackendPaymentMethod}).
 *
 * When the feature is disabled, or OP / a BLIK issuer isn't present, the list is returned
 * unchanged so BLIK keeps behaving as a regular OP issuer.
 *
 * @param paymentMethods - The list of available payment methods
 * @param standaloneBlikEnabled - The STANDALONE_BLIK_ENABLED toggle value
 * @param preferredPayment - The customer's preferred payment (used to move the preference to BLK)
 * @returns The payment methods list, with the virtual BLIK method injected when applicable
 */
export const injectStandaloneBlik = (
  paymentMethods: PaymentMethod[],
  standaloneBlikEnabled = false,
  preferredPayment?: PreferredPayment,
): PaymentMethod[] => {
  if (!standaloneBlikEnabled) {
    return paymentMethods;
  }

  const opIndex = paymentMethods.findIndex(pm => pm.paymentMethodShortName === 'OP');
  if (opIndex === -1) {
    return paymentMethods;
  }

  const op = paymentMethods[opIndex];
  const opIssuers = op.details?.issuers ?? [];
  const blikIssuer = opIssuers.find(isBlikIssuer);
  if (!blikIssuer) {
    return paymentMethods;
  }

  const remainingIssuers = opIssuers.filter(issuer => !isBlikIssuer(issuer));
  const isBlikPreferred =
    preferredPayment?.paymentMethodShortName === 'OP' &&
    isBlikIssuer(preferredPayment?.details?.lastIssuer);

  // OP without BLIK: keep only the remaining banks and reset its selected issuer if it used
  // to point at BLIK, so the OP tab no longer references the standalone method.
  const updatedOp: PaymentMethod = {
    ...op,
    preferredPayment: op.preferredPayment && !isBlikPreferred,
    details: {
      ...op.details,
      issuers: remainingIssuers,
      lastIssuer: isBlikIssuer(op.details?.lastIssuer)
        ? remainingIssuers[0]
        : op.details?.lastIssuer,
    },
  };

  // Virtual BLIK method: same setup as OP, but locked to the single BLIK issuer.
  const blikMethod: PaymentMethod = {
    ...op,
    paymentMethod: flippedSupportedPMs.BLK,
    paymentMethodShortName: 'BLK',
    default: false,
    preferredPayment: isBlikPreferred,
    details: {
      ...op.details,
      issuers: [blikIssuer],
      lastIssuer: blikIssuer,
    },
  };

  const result = [...paymentMethods];
  result.splice(opIndex, 1, updatedOp);
  result.splice(opIndex, 0, blikMethod); // BLIK immediately before OP
  return result;
};

/**
 * Function to get the initial payment methods
 *
 * This function transforms the initial payment state by:
 * 1. Applying the client key for Adyen which is stored locally and not provided by the checkout state
 * 2. Sets the Sepa IBAN to empty for the SEPA Direct Debit payment method as it only the last 4 digits are provided by the checkout state
 * 3. Splits BLIK out of Online Banking PL into a standalone virtual method when the feature is enabled
 *
 * @param paymentState - Payment state object
 * @param adyenClientKey - Adyen client key
 * @returns The transformed payment state object
 */
export const getInitialPaymentMethods = (paymentState?: PaymentState, adyenClientKey?: string) => {
  if (!paymentState) {
    return undefined;
  }
  const {
    paymentMethods: initPaymentMethods = [],
    cartHasMarketplaceItems,
    preferredPayment,
  } = paymentState;
  const standaloneBlikEnabled = !!paymentState?.featureFlags?.separate_blik_payment_method;

  const transformedPaymentMethods = initPaymentMethods?.map(paymentMethod => {
    const constructedPM = paymentMethod;
    if (
      isPaymentMethodDisabled(paymentMethod, paymentState?.customerType, cartHasMarketplaceItems)
    ) {
      return { ...paymentMethod, disabled: true };
    }
    if (
      constructedPM.paymentMethodShortName === 'K' &&
      constructedPM.details?.paymentSetupInfo?.clientInfo &&
      constructedPM.details?.paymentSetupInfo?.clientInfo?.pspName === 'ADYEN'
    ) {
      constructedPM.details.paymentSetupInfo.clientInfo.pspToken = adyenClientKey;
    }

    if (
      constructedPM.paymentMethodShortName === 'BS' &&
      constructedPM.details?.bankAccountDetails
    ) {
      constructedPM.details.bankAccountDetails.number = '';
    }
    return paymentMethod;
  });

  const paymentMethods = injectStandaloneBlik(
    transformedPaymentMethods,
    standaloneBlikEnabled,
    preferredPayment,
  );

  return {
    ...paymentState,
    paymentMethods,
  };
};

/**
 * Function to return status of payment method
 * Currently on the following 3 states are part of the payment method: 'default' | 'selected' | 'disabled' |
 * Tests would be added once the logic for getPaymentMethodStatus is finalized
 *
 * @param paymentMethod the payment tab should be displayed
 * @param selectedPayment the currently selected payment method
 * @param customerType the possible customer type values: EC, NC, GC, ''. This values are defined in shop-chekcout.
 * For more detailed info, please check https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/CustomerType.java#13-16
 * @returns list of payments in a similar format that documented here
 */

export const getPaymentMethodStatus = (
  paymentMethod: PaymentMethod,
  selectedPayment: PaymentMethod | undefined,
  customerType = '',
  cartHasMarketplaceItems = false,
  selectedIdentifier?: Nullable<string>,
): 'default' | 'selected' | 'disabled' => {
  if (isPaymentMethodDisabled(paymentMethod, customerType, cartHasMarketplaceItems)) {
    return 'disabled';
  } else if (
    paymentMethod.paymentMethodShortName === selectedPayment?.paymentMethodShortName &&
    (typeof selectedPayment?.order === 'number' ||
      bancontactPaymentMethods.includes(selectedPayment?.paymentMethodShortName || ''))
  ) {
    if (paymentMethod.paymentMethodShortName === 'RS' && selectedIdentifier) {
      return 'default';
    } else if (paymentMethod.paymentMethodShortName === 'K' && selectedIdentifier) {
      return 'default';
    } else {
      return 'selected';
    }
  } else {
    return 'default';
  }
};

/**
 * Function to return the status of a special case for Bancontact cards and mobile payments
 *
 * @param paymentMethod the payment method to be displayed
 * @param selectedPayment the currently selected payment method
 * @param customerType the possible customer type values: EC, NC, GC, ''. This values are defined in shop-chekcout.
 * For more detailed info, please check https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/CustomerType.java#13-16
 * @returns string Bancontact payment method status
 */

export const getBancontactPaymentMethodStatus = (
  paymentMethods: PaymentMethod[],
  selectedPayment: PaymentMethod | undefined,
  customerType = '',
  cartHasMarketplaceItems = false,
  selectedIdentifier?: Nullable<string>,
) => {
  const BM = paymentMethods?.find(pm => pm.paymentMethodShortName === 'BM');
  const BC = paymentMethods?.find(pm => pm.paymentMethodShortName === 'BC');
  const BMPMStatus =
    BM && getPaymentMethodStatus(BM, selectedPayment, customerType, cartHasMarketplaceItems);
  const BCPMStatus =
    BC && getPaymentMethodStatus(BC, selectedPayment, customerType, cartHasMarketplaceItems);
  let BCStatus: PaymentMethodStatus;

  if ((BMPMStatus === 'selected' || BCPMStatus === 'selected') && !selectedIdentifier) {
    BCStatus = 'selected';
  } else if (BMPMStatus === 'disabled' && BCPMStatus === 'disabled') {
    BCStatus = 'disabled';
  } else {
    BCStatus = 'default';
  }
  return BCStatus;
};

/**
 * Checks if a payment method is unavailable for guest customers.
 */
const isUnavailableForGuest = (availableForGuest: boolean, customerType: string): boolean =>
  !availableForGuest && (customerType === 'GC' || customerType === '');

/**
 * Function to determine if a payment method is disabled.
 *
 * This function evaluates whether a given payment method should be disabled based on its properties
 * and the type of customer. The following checks are performed:
 * 1. If the payment method is explicitly marked as disabled.
 * 2. If the payment method is not available for guest customers and the customer type is either
 *    guest customer ('GC') or not specified ('').
 * 3. If the payment method is Apple Pay ('AP') and Apple Pay is not supported.
 *
 * @param paymentMethod - The payment method to be evaluated.
 * @param customerType - The type of customer, which can be 'EC' (Existing Customer), 'NC' (New Customer),
 *                       'GC' (Guest Customer), or '' (Not Specified). These values are defined in shop-checkout.
 *                       For more detailed info, please check
 *                       https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/CustomerType.java#13-16
 * @returns boolean - Returns true if the payment method is disabled based on the evaluated conditions, otherwise false.
 */

export const isPaymentMethodDisabled = (
  paymentMethod: PaymentMethod,
  customerType = '',
  cartHasMarketplaceItems = false,
): boolean => {
  const { disabled, availableForGuest, marketplaceEligible, paymentMethodShortName } =
    paymentMethod;
  const isExplicitlyDisabled = disabled || (cartHasMarketplaceItems && !marketplaceEligible);
  const isApplePayUnsupported = paymentMethodShortName === 'AP' && !checkApplePaySupported();
  return [
    isExplicitlyDisabled,
    isUnavailableForGuest(availableForGuest, customerType),
    isApplePayUnsupported,
  ].some(Boolean);
};

/**
 * Function to select correct payment method based on the payment method status: 'default' | 'selected' | 'disabled'
 * Handles edge cases for Bancontact cards and mobile payments
 *
 * @param paymentMethods the payment methods list
 * @param paymentMethod the payment tab should be displayed
 * @param selectedPayment the currently selected payment method
 * @param customerType the possible customer type values: EC, NC, GC, ''. This values are defined in shop-chekcout.
 * For more detailed info, please check https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/CustomerType.java#13-16
 * @returns void
 */

export const onPaymentMethodClick =
  (
    paymentMethods: PaymentMethod[],
    paymentMethod: PaymentMethod,
    selectedPayment: PaymentMethod | undefined,
    customerType: string,
    setSelectedPayment: (paymentMethod: any) => void,
    deselectCreditCard: (() => void) | undefined,
    standaloneBlikEnabled = false,
  ) =>
  () => {
    const { paymentMethodShortName } = paymentMethod;
    const isPaymentMethodDisabled =
      getPaymentMethodStatus(paymentMethod, selectedPayment, customerType) === 'disabled';
    if (paymentMethodShortName === 'BM' && isPaymentMethodDisabled) {
      setSelectedPayment(paymentMethods.find(pm => pm.paymentMethodShortName === 'BC'));
    } else if (paymentMethodShortName === 'BC' && isPaymentMethodDisabled) {
      setSelectedPayment(paymentMethods.find(pm => pm.paymentMethodShortName === 'BM'));
    } else {
      let lastIssuer = paymentMethod.details?.lastIssuer || selectedPayment?.details?.lastIssuer;
      if (
        standaloneBlikEnabled &&
        selectedPayment?.paymentMethodShortName === 'BLK' &&
        paymentMethod.paymentMethodShortName === 'OP'
      ) {
        lastIssuer = paymentMethod.details?.issuers?.[0];
      }
      setSelectedPayment({
        ...paymentMethod,
        details: {
          ...(paymentMethod?.details ?? {}),
          lastIssuer,
        },
      });
      deselectCreditCard?.();
    }
  };

/**
 * Separate autoshipment and non-autoshipment eligible payment methods into two different lists
 *
 * @param paymentMethodList list of payment methods with additional details
 * @returns autoshipment eligible and nonautoshipment eligible payment lists
 */
export const separatePaymentLists = (paymentMethodList: any[]) => {
  const eligible = paymentMethodList.filter(
    (payment: any) => !payment.disabled && payment.autoshipmentEligible,
  );
  const nonEligible = paymentMethodList.filter(
    (payment: any) => !payment.disabled && !payment.autoshipmentEligible,
  );
  return { eligible, nonEligible };
};

/**
 *
 * @param selectedPM Currenctly selected payment method
 * @param preferredPM preferred payment method saved in the checkout state
 * @param track track function
 * @returns void
 */
export const trackChangePM = (
  selectedPM?: PreferredPayment,
  preferredPM?: PreferredPayment,
  track?: TrackingFn,
) => {
  const { details: selectedPMdetails, paymentMethodShortName: selectedPMShortName } =
    selectedPM ?? {};
  const { details: preferredPMdetails, paymentMethodShortName: preferredPMShortName } =
    preferredPM ?? {};

  const { bankAccountDetails: selectedPMBankAccountDetails } = selectedPMdetails ?? {};
  const { number: selectedPMNumber, owner: selectedPMOwner } = selectedPMBankAccountDetails ?? {};

  const { bankAccountDetails: preferredPMBankAccountDetails } = preferredPMdetails ?? {};
  const { number: preferredPMNumber, owner: preferredPMOwner } =
    preferredPMBankAccountDetails ?? {};

  const isSamePMSelected = selectedPMShortName === preferredPMShortName;
  const isBSSelected = isSamePMSelected && selectedPMShortName === 'BS';

  if (!selectedPMShortName) {
    return;
  }

  if (
    (isBSSelected && selectedPMNumber && selectedPMNumber !== preferredPMNumber) ||
    (isBSSelected && selectedPMOwner && selectedPMOwner !== preferredPMOwner)
  ) {
    return track?.('ChangePaymentMethod', 'BS');
  }

  if (!isSamePMSelected && selectedPMShortName === 'K') {
    return track?.('ChangePaymentMethodPCI', 'AK');
  }

  if (!isSamePMSelected) {
    return track?.('ChangePaymentMethod', selectedPMShortName);
  }
};

export const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }

  if (a == null || b == null) {
    return false;
  }
  if (typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }

  const isAArray = Array.isArray(a);
  const isBArray = Array.isArray(b);

  if (isAArray !== isBArray) {
    return false;
  }

  if (isAArray && isBArray) {
    if ((a as unknown[]).length !== (b as unknown[]).length) {
      return false;
    }
    return (a as unknown[]).every((item, index) => isDeepEqual(item, (b as unknown[])[index]));
  }

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  return aKeys.every(key =>
    isDeepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
};

export const arePaymentDetailsEqual = (
  preferredPayment?: PreferredPayment,
  selectedPayment?: PaymentMethod,
) => {
  if (!preferredPayment || !selectedPayment) {
    return false;
  }

  const preferredDetails = preferredPayment.details ?? {};
  const selectedDetails = selectedPayment.details ?? {};
  const paymentDetailsKeys = [
    'paymentAdjustment',
    'adyenPaymentMethodResponse',
  ] as (keyof PaymentMethod['details'])[];

  return paymentDetailsKeys.reduce((acc: boolean, key: keyof PaymentMethod['details']) => {
    const preferredValue = preferredDetails[key] ?? undefined;
    const selectedValue = selectedDetails[key] ?? undefined;
    let isEqual = false;
    if (typeof preferredValue === 'object' && typeof selectedValue === 'object') {
      isEqual = isDeepEqual(preferredValue, selectedValue);
    } else {
      isEqual = preferredValue === selectedValue;
    }

    return acc && isEqual;
  }, true);
};

export const isSameStoredPaymentMethodSelected = (
  preferredPayment?: PreferredPayment,
  selectedPayment?: PaymentMethod,
  selectedIdentifier?: Nullable<string>,
) => {
  if (!['BC', 'K', 'RS'].includes(preferredPayment?.paymentMethodShortName as string)) {
    return true;
  }
  const isSameSelectedPayment =
    preferredPayment?.paymentMethodShortName === selectedPayment?.paymentMethodShortName;
  const isSameStoredCardSelected =
    ['BC', 'K'].includes(preferredPayment?.paymentMethodShortName as string) &&
    preferredPayment?.details?.cards?.length &&
    preferredPayment?.details?.cards?.[0]?.pspIdentifier === selectedIdentifier;
  const isSameStoredBankAccountSelected =
    preferredPayment?.paymentMethodShortName === 'RS' &&
    preferredPayment?.details?.storedBankAccounts?.length &&
    preferredPayment?.details?.storedBankAccounts?.[0]?.pspTokenIdentifier === selectedIdentifier;

  return (
    (isSameSelectedPayment && isSameStoredCardSelected) ||
    (isSameSelectedPayment && isSameStoredBankAccountSelected)
  );
};
